// One count line → one purchase-order line, for BOTH runtimes.
//
// The Ordering Hub preview (`services/orderingHubService.js`) and the server's
// bucket builder (`purchase-order-dispatch#buildVendorBucketsFromCount`) each
// had their own copy of this leg, and the copies drifted in exactly the way
// two copies always do. Draft CATS-20260821-0002 is the record of it: four
// Southern Glazer's liquors, every one of them carrying a frozen
// `normalized_unit_cost_snapshot` (Tito's 22.95, Grey Goose 31.95, Jameson
// 34.95, Campari 36.50) and an active, priced purchase option, ordered at
// **$0.00** — and at 2.9 and 0.3 of a bottle.
//
// Two separate faults produced that, and this module exists to make both
// unrepresentable.
//
// 1. **Zero stopped the price fallback.** The old chain was
//    `unit_price_snapshot ?? snapshot.unit_price ?? each_price ?? case_price ?? 0`.
//    `??` is FIRST NON-NULL, and a legacy row's `unit_price` is `0` — a real
//    value to `??`, so the chain stopped there and never reached the 22.95
//    sitting one rung further down. Price selection here is FIRST VALID
//    POSITIVE, and the rungs the count close deliberately froze for exactly
//    this purpose (`normalized_unit_cost_snapshot`,
//    `snapshot_at_close.normalized_unit_cost`) are read before the legacy
//    columns, not after them.
//
// 2. **A price is denominated in something.** A PO's `unit_price` means the
//    price of ONE thing the vendor accepts as an order unit — one bottle on a
//    bottle line, one case on a case line. So every rung here is gated on the
//    order unit: a per-count-unit figure may only price a line whose order
//    unit IS that count unit, and a case price may only price a line whose
//    order unit is that case. When the evidence cannot be stated in the
//    line's own unit the answer is PRICE UNAVAILABLE — a state the buyer can
//    see and fill in — never a fabricated number, and never $0.00, which
//    reads as "free".
//
// Nothing here is new arithmetic. Package/MOQ/increment/fractional rules come
// from `planTypedOrderLine` (orderRecalc) over `orderPackagesForNeed`, and the
// no-option quantity rule from `legalizeOrderPackages` — the same order-step
// authority every other buying surface uses.
//
// Pure ESM. No Deno APIs, no I/O, no browser APIs.

import { planTypedOrderLine } from "./orderRecalc.mjs";
import { legalizeOrderPackages } from "./parAndMoq.mjs";
import { canonicalPackWord } from "./packText.mjs";
import { normalizeUnit } from "../menu-recipe-costing/units.mjs";

const text = (v) => (v == null ? "" : String(v).trim());

const positive = (v) => {
  // Number, not parseFloat: "22.95 per bottle" is prose, not a price, and
  // parseFloat would silently accept it as 22.95.
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const round2 = (n) => Math.round(n * 100) / 100;

/** Where a PO line's unit price came from. Frozen onto the line. */
export const PO_PRICE_SOURCES = Object.freeze({
  PACKAGE_PRICE: "purchase_option_package_price",
  UNIT_PRICE_SNAPSHOT: "count_items.unit_price_snapshot",
  NORMALIZED_COST_SNAPSHOT: "count_items.normalized_unit_cost_snapshot",
  CLOSE_NORMALIZED_COST: "snapshot_at_close.normalized_unit_cost",
  ITEM_NORMALIZED_COST: "item_snapshot.normalized_unit_cost",
  SNAPSHOT_UNIT_PRICE: "snapshot.unit_price",
  SNAPSHOT_EACH_PRICE: "snapshot.each_price",
  SNAPSHOT_CASE_PRICE: "snapshot.case_price",
});

/** Why no price could be stated per the line's own order unit. */
export const PO_PRICE_UNAVAILABLE_REASONS = Object.freeze({
  NO_POSITIVE_PRICE: "no_positive_price",
  UNIT_BASIS_AMBIGUOUS: "unit_basis_ambiguous",
});

export const PO_PRICE_UNAVAILABLE_TEXT = Object.freeze({
  no_positive_price: "No price on record for this item — enter one before sending.",
  unit_basis_ambiguous:
    "The prices on record are for a different unit than this line is ordered in — enter the price of one order unit.",
});

/** The label every surface shows instead of $0.00 when a price is missing. */
export const PRICE_UNAVAILABLE_LABEL = "PRICE UNAVAILABLE";

/**
 * Two unit words naming the same thing.
 *
 * "Bottles" and "bottle" are one unit written twice; so are "gal" and
 * "gallon". Anything the engine can measure is compared on its canonical key,
 * and everything else (container words, product nouns) on its singular form.
 */
export function sameOrderUnit(left, right) {
  const a = text(left).toLowerCase();
  const b = text(right).toLowerCase();
  if (!a || !b) return false;
  if (a === b) return true;
  const na = normalizeUnit(a);
  const nb = normalizeUnit(b);
  // A measurable unit's identity is its base and factor, not the word: "gal"
  // and "gallon" are one unit spelled twice, and normalizeUnit keeps the raw
  // spelling as its key.
  if (na.base && na.base === nb.base && na.factor === nb.factor) return true;
  return (canonicalPackWord(a) || a) === (canonicalPackWord(b) || b);
}

/** True when the word names a case/carton the count unit is packed into. */
const isCaseWord = (unit) => {
  const word = canonicalPackWord(text(unit).toLowerCase());
  return word === "case" || word === "carton" || word === "cs";
};

/**
 * May a fraction of one order unit be ordered?
 *
 * Continuous measures divide — 2.4 lb of beef is a real order. Discrete
 * things do not: 0.3 of a bottle is not an order anyone can fill, and
 * "0.3 bottle" on a vendor's PO is how a line comes back unfilled. Unknown
 * words are treated as discrete, because the words we cannot classify are
 * product nouns ("sleeve", "portion"), not measures.
 */
export function orderUnitAllowsFraction(unit) {
  const kind = normalizeUnit(text(unit).toLowerCase()).kind;
  return kind === "mass" || kind === "volume";
}

/**
 * The count unit a snapshot's per-unit prices are denominated in.
 *
 * `buildSnapshotAtClose` writes `count_unit` alongside `normalized_unit_cost`
 * for exactly this reason; older snapshots only carry the item's `unit`.
 */
export function snapshotCountUnit(snapshot) {
  const snap = snapshot && typeof snapshot === "object" ? snapshot : {};
  return text(snap.count_unit) || text(snap.unit) || text(snap.par_unit) || null;
}

/**
 * The price of ONE order unit, from the count's frozen evidence.
 *
 * FIRST VALID POSITIVE, never first-non-null, and every rung gated on the
 * unit it is denominated in:
 *
 *   1. `count_items.unit_price_snapshot`          per count unit
 *   2. `count_items.normalized_unit_cost_snapshot` per count unit
 *   3. `snapshot_at_close.normalized_unit_cost`    per its own `count_unit`
 *   4. `item_snapshot.normalized_unit_cost` / `verified_unit_cost`
 *   5. `snapshot.unit_price`                       per count unit
 *   6. `snapshot.each_price`                       per count unit
 *   7. `snapshot.case_price`                       per CASE — usable only
 *      when one case IS one order unit
 *   8. PRICE UNAVAILABLE
 *
 * Rungs 1–6 are refused outright when the line's order unit is not the count
 * unit those figures describe: substituting a normalized per-ounce cost into
 * a line ordered by the case is the same class of error as reading 0 as a
 * price, and it is the more expensive one.
 *
 * @returns {{ available:boolean, unitPrice:number|null,
 *             source:string|null, reason:string|null, priceUnit:string|null }}
 */
export function resolveDirectOrderUnitPrice({
  orderUnit,
  snapshot,
  itemSnapshot = null,
  unitPriceSnapshot = null,
  normalizedUnitCostSnapshot = null,
} = {}) {
  const snap = snapshot && typeof snapshot === "object" ? snapshot : {};
  const item = itemSnapshot && typeof itemSnapshot === "object" ? itemSnapshot : {};
  const unit = text(orderUnit);
  const countUnit = snapshotCountUnit(snap);
  // No order unit on the line and no count unit in the snapshot: there is
  // nothing to denominate a price in, so nothing can be verified against it.
  // The count-unit rungs still apply when the two agree, which is every
  // legacy line — its order unit IS the item's count unit.
  const perCountUnitApplies = Boolean(unit && countUnit && sameOrderUnit(unit, countUnit))
    || Boolean(!unit && countUnit)
    || Boolean(unit && !countUnit);

  const found = (unitPrice, source) => ({
    available: true, unitPrice, source, reason: null, priceUnit: unit || countUnit || null,
  });

  let sawPositive = false;
  const rung = (value) => {
    const n = positive(value);
    if (n !== null) sawPositive = true;
    return n;
  };

  // A normalized cost that is really a CASE price.
  //
  // `buildSnapshotAtClose` falls back to `case_price` when an item has no
  // verified cost, so `normalized_unit_cost` — a field whose contract is
  // "per count unit" — can hold the price of a whole case. Harmless when the
  // case holds one count unit (a $22.95 "case" of one 750 mL bottle IS
  // $22.95 a bottle, which is the shape that produced this bug). Not harmless
  // when the case holds 24: pricing each bottle at the case price is a worse
  // error than pricing it at zero, because it is plausible.
  //
  // When the snapshot's own case facts contradict the figure, the rung is
  // refused and the answer becomes NEEDS REVIEW — never case_price ÷
  // case_quantity, which would invent a per-unit price the store's own
  // valuation never used.
  const caseQuantity = positive(snap.case_quantity ?? snap.case_qty);
  const snapCasePrice = positive(snap.case_price);
  const contradictedByCase = (value) => (
    value !== null
    && snapCasePrice !== null
    && caseQuantity !== null
    && caseQuantity > 1
    && Math.abs(value - snapCasePrice) < 0.005
    && !isCaseWord(unit)
    && !isCaseWord(countUnit)
  );
  const perUnitRung = (value) => {
    const n = rung(value);
    return contradictedByCase(n) ? null : n;
  };

  const perCountUnit = [
    [rung(unitPriceSnapshot), PO_PRICE_SOURCES.UNIT_PRICE_SNAPSHOT],
    [perUnitRung(normalizedUnitCostSnapshot), PO_PRICE_SOURCES.NORMALIZED_COST_SNAPSHOT],
    [perUnitRung(snap.normalized_unit_cost), PO_PRICE_SOURCES.CLOSE_NORMALIZED_COST],
    // The live snapshot taken during the count is the raw inventory row: it
    // carries `verified_unit_cost` rather than the derived
    // `normalized_unit_cost` the close snapshot computes from it.
    [perUnitRung(item.normalized_unit_cost ?? item.verified_unit_cost), PO_PRICE_SOURCES.ITEM_NORMALIZED_COST],
    [rung(snap.unit_price), PO_PRICE_SOURCES.SNAPSHOT_UNIT_PRICE],
    [rung(snap.each_price), PO_PRICE_SOURCES.SNAPSHOT_EACH_PRICE],
  ];

  if (perCountUnitApplies) {
    for (const [value, source] of perCountUnit) {
      if (value !== null) return found(value, source);
    }
  }

  // A case price prices a CASE. It may stand as this line's unit price only
  // when the line is ordered by that case — either the order unit says so, or
  // the case holds exactly one count unit, which makes the two the same
  // thing. A case of 24 with a $22.95 case price is NOT a $22.95 bottle, and
  // guessing which it is here is how a bar orders 24× what it meant to.
  const casePrice = rung(snap.case_price);
  if (casePrice !== null) {
    const caseQty = snap.case_quantity ?? snap.case_qty;
    const holdsOne = positive(caseQty) === 1;
    if (isCaseWord(unit) || (perCountUnitApplies && holdsOne) || (isCaseWord(countUnit) && perCountUnitApplies)) {
      return found(casePrice, PO_PRICE_SOURCES.SNAPSHOT_CASE_PRICE);
    }
  }

  return {
    available: false,
    unitPrice: null,
    source: null,
    reason: sawPositive
      ? PO_PRICE_UNAVAILABLE_REASONS.UNIT_BASIS_AMBIGUOUS
      : PO_PRICE_UNAVAILABLE_REASONS.NO_POSITIVE_PRICE,
    priceUnit: unit || countUnit || null,
  };
}

/**
 * A count-derived need turned into a quantity the vendor can actually fill,
 * on a line that names no vendor product.
 *
 * With no purchase option there is no MOQ, no increment and no stated
 * fractional permission — nothing may be invented for those. What is still
 * true is the unit itself: 2.9 bottles and 0.3 bottle are not orders. The
 * one order-step authority (`legalizeOrderPackages`) does the rounding, at a
 * whole-unit grid for discrete units and not at all for measures.
 *
 * @returns {{ quantity:number, adjusted:boolean, reason:string|null }}
 */
export function legalizeDirectOrderQuantity({ quantity, orderUnit } = {}) {
  const allowsFractional = orderUnitAllowsFraction(orderUnit);
  const result = legalizeOrderPackages({
    packages: quantity,
    moqPackages: null,
    orderIncrement: null,
    allowsFractional,
  });
  return {
    quantity: result.packages,
    adjusted: result.adjusted,
    reason: result.adjusted ? "whole_order_unit" : null,
  };
}

/**
 * The whole line: what to order, in whose terms, at what price.
 *
 * Given the count's need for one item and (when one could be resolved) the
 * vendor product it will be bought as, produce the PO line both the preview
 * and the created draft render — one answer, computed once.
 *
 * @param {object}  args
 * @param {number}  args.orderQuantity  the count's need, in the item's count unit
 * @param {object}  args.snapshot       snapshot_at_close, else item_snapshot
 * @param {object} [args.itemSnapshot]  the count's live item_snapshot, if separate
 * @param {number} [args.unitPriceSnapshot]
 * @param {number} [args.normalizedUnitCostSnapshot]
 * @param {object} [args.option]        the resolved purchase_options row, or null
 * @param {string} [args.optionSelectionReason] why that option (resolveDirectOrderOption)
 * @param {string} [args.packSizeFallback] the count's own frozen pack text, for
 *                                         legacy lines (resolvePoLinePackSize)
 */
export function planCountOrderLine({
  orderQuantity,
  snapshot,
  itemSnapshot = null,
  unitPriceSnapshot = null,
  normalizedUnitCostSnapshot = null,
  option = null,
  optionSelectionReason = null,
  packSizeFallback = null,
} = {}) {
  const snap = snapshot && typeof snapshot === "object" ? snapshot : {};
  const needUnit = text(snap.unit) || text(snap.par_unit);
  const typed = Number(orderQuantity);

  // ── The vendor's own terms, when a vendor product was resolved ───────────
  const typedPlan = option
    ? planTypedOrderLine({ typedQuantity: typed, needUnit, option })
    : null;

  if (typedPlan?.applicable) {
    return {
      usedOption: true,
      purchaseOptionId: option.id != null ? String(option.id) : null,
      optionDeclinedReason: null,
      optionSelectionReason,
      unit: typedPlan.unit || "package",
      packSize: typedPlan.packSize,
      orderQuantity: typedPlan.packages,
      quantityReceived: typedPlan.quantityReceived,
      overage: typedPlan.overage,
      unitPrice: typedPlan.packagePrice,
      lineTotal: typedPlan.lineTotal,
      priceAvailable: true,
      priceSource: PO_PRICE_SOURCES.PACKAGE_PRICE,
      priceReason: null,
      quantityAdjusted: typedPlan.adjusted,
      quantityAdjustmentReason: typedPlan.adjusted ? "vendor_order_rules" : null,
      snapshot: buildLineSnapshot({
        option,
        optionSelectionReason,
        priceSource: PO_PRICE_SOURCES.PACKAGE_PRICE,
        priceAvailable: true,
        priceReason: null,
        priceUnit: typedPlan.unit || "package",
        adjustment: typedPlan.adjusted
          ? {
            typed_quantity: typed,
            typed_unit: needUnit || null,
            quantity_received: typedPlan.quantityReceived,
            overage: typedPlan.overage,
            driver: "vendor_order_rules",
          }
          : null,
      }),
    };
  }

  // ── No usable vendor product: the count's own evidence, unit-checked ─────
  const orderUnit = needUnit;
  const legalQty = legalizeDirectOrderQuantity({ quantity: typed, orderUnit });
  const price = resolveDirectOrderUnitPrice({
    orderUnit,
    snapshot: snap,
    itemSnapshot,
    unitPriceSnapshot,
    normalizedUnitCostSnapshot,
  });

  return {
    usedOption: false,
    purchaseOptionId: null,
    optionDeclinedReason: typedPlan ? typedPlan.reason : (option ? "no_plan" : "no_option"),
    optionSelectionReason,
    unit: orderUnit,
    packSize: packSizeFallback,
    orderQuantity: legalQty.quantity,
    quantityReceived: null,
    overage: null,
    unitPrice: price.available ? price.unitPrice : null,
    lineTotal: price.available ? round2(legalQty.quantity * price.unitPrice) : null,
    priceAvailable: price.available,
    priceSource: price.source,
    priceReason: price.reason,
    quantityAdjusted: legalQty.adjusted,
    quantityAdjustmentReason: legalQty.reason,
    snapshot: buildLineSnapshot({
      option: null,
      optionSelectionReason,
      optionDeclinedReason: typedPlan ? typedPlan.reason : (option ? "no_plan" : "no_option"),
      priceSource: price.source,
      priceAvailable: price.available,
      priceReason: price.reason,
      priceUnit: price.priceUnit,
      adjustment: legalQty.adjusted
        ? {
          typed_quantity: typed,
          typed_unit: orderUnit || null,
          quantity_received: null,
          overage: null,
          driver: "whole_order_unit",
        }
        : null,
    }),
  };
}

/**
 * What the line freezes about the product it names.
 *
 * A PO line that says only "Tito's, 3, $22.95" cannot be reconciled against
 * an invoice, re-priced, or re-ordered — the vendor product it was written
 * against is the missing half. `purchase_order_items.snapshot` is where the
 * allocation RPCs already put `vendor_sku` / `vendor_item_name`, so the
 * direct-order path writes the same keys rather than inventing a second
 * shape.
 */
export function buildLineSnapshot({
  option = null,
  optionSelectionReason = null,
  optionDeclinedReason = null,
  priceSource = null,
  priceAvailable = true,
  priceReason = null,
  priceUnit = null,
  adjustment = null,
} = {}) {
  const snapshot = {
    price_provenance: {
      source: priceSource,
      available: priceAvailable,
      reason: priceReason,
      unit: priceUnit,
    },
  };
  if (option) {
    if (text(option.vendor_sku)) snapshot.vendor_sku = text(option.vendor_sku);
    if (text(option.vendor_description)) {
      snapshot.vendor_item_name = text(option.vendor_description);
      snapshot.vendor_description = text(option.vendor_description);
    }
    if (option.vendor_id != null) snapshot.vendor_id = String(option.vendor_id);
    if (text(option.purchase_unit)) snapshot.purchase_unit = text(option.purchase_unit);
    if (text(option.pack_description)) snapshot.pack_description = text(option.pack_description);
    if (positive(option.package_price) !== null) snapshot.package_price = Number(option.package_price);
    snapshot.order_rules = {
      moq_packages: option.moq_packages ?? null,
      order_increment: option.order_increment ?? null,
      allows_fractional_packages: option.allows_fractional_packages === true,
      qty_per_package: option.qty_per_package ?? null,
    };
  }
  if (optionSelectionReason) snapshot.option_selection_reason = optionSelectionReason;
  if (optionDeclinedReason) snapshot.option_declined_reason = optionDeclinedReason;
  if (adjustment) snapshot.order_rule_adjustment = adjustment;
  return snapshot;
}
